import { Trans } from '@lingui/react'
import { arrayOf, bool, number, shape, string } from 'prop-types'
import { compose } from 'redux'

import { DOCUMENT_TYPE } from 'shared/consts'
import { OFFER_TYPE_MIRAKL, OFFER_TYPE_SAE } from 'shared/consts/marketplace'
import { withI18n } from 'shared/utils/withI18n'
import Button from 'views/components/atoms/Button'
import Flag from 'views/components/atoms/Flag'
import Form from 'views/components/atoms/Form'
import IconCart from 'views/components/atoms/Icon/IconCart'
import Price from 'views/components/atoms/Price'
import NowLabel from 'views/components/molecules/NowWidget/NowLabel'
import ProductSpecialLabel from 'views/components/molecules/ProductSpecialLabel'
import StarRating from 'views/components/molecules/StarRating'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import ProductPrice from 'views/components/organisms/ProductPrice'
import ProductStatus from 'views/components/organisms/ProductStatus'
import { SELLER_MODAL_ID } from 'views/components/organisms/SellerModal/names'
import { promotionTypeMapping } from 'views/consts/promotions'
import { getSellerModalHook } from 'views/containers/browser/SellerModalContainer/clientsideHook'
import withMountPoints from 'views/enhancers/withMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import {
  i18nType,
  mountPointsType,
  publicRuntimeConfigType,
} from 'views/utils/propTypes'
import './OfferItem.scss'

const OfferItem = props => {
  const {
    basePrice,
    nowProductGroup,
    offerId,
    offerType,
    prices,
    seller: { sellerId, displayName, sellerGrade, sellerRatingCount },
    shippingPrice,
    stockStatus,
    productId,
    mountPoints,
    offerPromotion,
    marketplaceEnabled,
    i18n,
    redPointsMultiplierIsValid,
    multiplierRP,
    isBestOffer,
  } = props

  const basePriceTemplate = () => {
    if (basePrice === null) {
      return ''
    }

    const { unit, value } = basePrice

    return (
      <div className="o-OfferItem__font-size-base-small">
        <Price {...basePrice} symbolRight /> / {value}&nbsp;{unit}
      </div>
    )
  }

  const shippingText = (
    <span data-qa-id="shipping-cost">
      {shippingPrice.amount > 0 ? (
        <Trans
          id="general.label.additionalShippingCost"
          values={{ amount: <Price {...shippingPrice} /> }}
        />
      ) : (
        <Trans id="general.label.freeShipping" />
      )}
    </span>
  )

  const sellerInfo = () => (
    <div>
      <div className="l-flex u-padding-x-small--bottom@large u-no-padding">
        <p className="u-font-weight--bold u-no-margin o-OfferItem__font-size-base-small o-OfferItem__font-size-base@large">
          <Trans id="offerItem.label.saleAndShipping" />
        </p>
      </div>
      <div className="u-color--gray-medium l-flex o-OfferItem__font-size-base-small l-flex--wrap o-OfferItem__font-size-base@large">
        {offerType === OFFER_TYPE_MIRAKL ? (
          <div
            className="a-link a-link--no-underline u-font-weight--bold u-padding-x-small--right o-OfferItem__ratings__seller"
            data-a11y-dialog-show={SELLER_MODAL_ID}
            data-clientside-hook={getSellerModalHook(sellerId)}
            data-product-id={productId}
          >
            <div
              data-qa-id="seller-name"
              className="o-OfferItem__ratings__seller__text"
            >
              {displayName}
            </div>

            {sellerRatingCount > 0 && (
              <StarRating
                rating={sellerGrade}
                ratingCount={sellerRatingCount}
                hasSuperScript
              />
            )}
          </div>
        ) : (
          <div
            className="u-font-weight--bold u-padding-x-small--right o-OfferItem__ratings__seller__text"
            data-qa-id="seller-name"
          >
            <Trans id="general.shopName" />
          </div>
        )}
      </div>
    </div>
  )

  const addToCartButton = () => {
    return (
      <Form
        name={`buy-offer-from-${offerId}`}
        action={mountPoints.getAddToCart()}
        data-clientside-hook="chargeUrlSearchParams"
        data-qa-id="add-to-cart-button"
      >
        <input name="cartAction" type="hidden" value="add" />
        <input name="product" type="hidden" value={productId} />
        <input name="eventType" type="hidden" value="conversion" />
        <input name="offer" type="hidden" value={offerId} />
        <input name="eventName" type="hidden" value="addToCart_OfferItem" />
        <input name="quantity" type="hidden" value="1" />
        <input
          type="hidden"
          name="prescriptionType"
          value={DOCUMENT_TYPE.NO_PRESCRIPTION}
        />
        <Button
          name="add-to-cart"
          type="submit"
          className="o-OfferItem__addtocart__button"
        >
          <TextWithIcon
            iconClassName="u-margin-x-small--right"
            icon={<IconCart />}
          >
            <span className="u-padding-x-small">
              <Trans id="general.button.label.addToCart" />
            </span>
          </TextWithIcon>
        </Button>
      </Form>
    )
  }

  let displayNowWidget = nowProductGroup === 'sameday_nextday'

  if (displayNowWidget && marketplaceEnabled) {
    displayNowWidget = offerType === OFFER_TYPE_SAE
  }

  const renderNowLabel = () => {
    if (displayNowWidget) {
      return (
        <li
          className="l-flex l-flex__primary u-margin-small--right"
          key="nowWidget"
          data-clientside-hook="nowWidget"
          data-product-identifier={productId}
        >
          <NowLabel outlinedIcon />
        </li>
      )
    }

    return null
  }

  const getPromotionFromPromotionType = promotionType =>
    promotionTypeMapping[promotionType]

  const renderOfferPromotion = () => {
    if (
      offerPromotion?.promotionType &&
      marketplaceEnabled &&
      offerType === OFFER_TYPE_SAE
    ) {
      const promotion = getPromotionFromPromotionType(
        offerPromotion.promotionType
      )
      return (
        <li className="u-margin-small--right a-box--white" key={promotion}>
          <ProductSpecialLabel promotion={promotion} />
        </li>
      )
    }

    return null
  }

  const renderRedPointLabel = () => {
    if (redPointsMultiplierIsValid && multiplierRP > 1 && marketplaceEnabled) {
      return (
        <div
          className="u-margin-small--right a-box--white"
          data-clientside-hook="redPointsMultiplier"
        >
          <Flag
            label={`${multiplierRP}x ${i18n._(
              'general.label.product.redPoints'
            )}`}
            redPointsMultiplier
          />
        </div>
      )
    } else {
      return null
    }
  }

  const renderOfferItemLabels = () => {
    return (
      <div className="u-padding--left u-padding-large--left@large o-OfferItem__labels">
        <ul className="a-list-reset l-flex--inline o-ProductLabels">
          {renderRedPointLabel()}
          {renderNowLabel()}
          {renderOfferPromotion()}
        </ul>
      </div>
    )
  }

  return (
    <div>
      {renderOfferItemLabels()}
      <div className="o-OfferItem l-grid__item u-background-color--background u-margin-large--bottom a-box u-padding-large--ends">
        {displayNowWidget && (
          <div className="u-padding--bottom o-OfferItem__widget"> </div>
        )}
        <div className="l-grid__item l-col--6-of-12 u-no-padding l-col--3-of-12@medium o-OfferItem__Prices">
          <ProductPrice prices={prices} displayInfo={false} />
          {basePriceTemplate()}
          <p className="a-p u-color--grey u-padding-x-small--bottom u-no-margin">
            {shippingText}
          </p>
          {isBestOffer && (
            <p className="u-color--info-dark u-font-weight--bold u-no-margin o-OfferItem__font-size-base-small o-OfferItem__Prices__BestOffer">
              <Trans id="offerItem.label.bestOffer" />
            </p>
          )}
        </div>

        <div className="l-grid__item l-col--6-of-12 l-col--3-of-12@medium u-text-align--right u-text-align--inherit@medium o-OfferItem__shipping">
          <div
            className="u-padding-x-small--bottom u-no-margin"
            data-qa-id="stock-status"
          >
            <ProductStatus
              stockStatus={stockStatus}
              className="u-padding-x-small--bottom u-no-margin"
            />
          </div>
        </div>

        <div className="l-grid__item l-col--6-of-12@medium u-hidden@large u-no-padding u-padding--top o-OfferItem__ratings-tablet">
          {addToCartButton()}
          <div className="u-padding--top">{sellerInfo()}</div>
        </div>

        <div className="l-grid__item l-col--4-of-12@large u-no-padding o-OfferItem__ratings-desktop">
          {sellerInfo()}
        </div>

        <div className="l-col--2-of-12@large u-no-padding o-OfferItem__addtocart-desktop">
          {addToCartButton()}
        </div>
      </div>
    </div>
  )
}

OfferItem.propTypes = {
  mountPoints: mountPointsType.isRequired,
  basePrice: shape({}),
  bestPrice: bool,
  nowProductGroup: string,
  offerId: string.isRequired,
  offerType: string.isRequired,
  prices: arrayOf(shape({})).isRequired,
  i18n: i18nType.isRequired,
  productId: string.isRequired,
  seller: shape({}),
  shippingPrice: shape({}).isRequired,
  stockStatus: shape({}),
  marketplaceEnabled: bool.isRequired,
  offerPromotion: shape({
    promotionOffer: string,
    promotionOfferPostfix: string,
    promotionType: string,
  }),
  multiplierRP: number,
  redPointsMultiplierIsValid: bool,
  isBestOffer: bool,
  publicRuntimeConfig: shape(publicRuntimeConfigType),
}

OfferItem.defaultProps = {
  basePrice: null,
  bestPrice: true,
  nowProductGroup: 'none',
  seller: {},
  stockStatus: {
    status: '',
    statusColor: 'green',
    statusReason: 'general.label.product.available',
  },
  offerPromotion: null,
  multiplierRP: 1,
  redPointsMultiplierIsValid: false,
  isBestOffer: false,
  publicRuntimeConfig: {},
}

export default compose(
  withI18n(),
  withMountPoints,
  withShopAndRuntimeConfig
)(OfferItem)
